/**
 * Live traffic, the privacy-preserving way. Phones never send a trajectory: they
 * send the average speed they saw inside one ~150 m cell (geohash-7) in one
 * heading octant, unsigned, after a random delay, with no identifier. The server
 * keeps one running mean per cell+octant. Nothing in the database can say who
 * drove where, and nothing can be replayed into a track.
 */
import { encodeGeohash } from "./geohash.js";

export type TrafficSample = { g: string; o: number; k: number; n: number };
/** What the feed publishes per gh5 cell. */
export type TrafficItem = { g: string; o: number; k: number; n: number; t: number };

/** 8 heading octants: 0 = north ±22.5°, 2 = east, 4 = south, 6 = west. */
export function octantOf(course: number): number {
  return Math.round((((course % 360) + 360) % 360) / 45) % 8;
}

export function trafficKey(gh7: string, octant: number): string {
  return `${gh7}:${octant}`;
}

export function cellOf(lat: number, lon: number): string {
  return encodeGeohash(lat, lon, 7);
}

/** Running mean: fresh cells blend, stale cells restart from the new sample. */
export const TRAFFIC_FRESH_MS = 15 * 60_000;
export function blendSpeed(prevKmh: number, prevAt: number, sampleKmh: number, nowMs: number): number {
  if (nowMs - prevAt > TRAFFIC_FRESH_MS) return sampleKmh;
  return prevKmh * 0.7 + sampleKmh * 0.3;
}

/** Below this the cell is "slow" (drawn); below SLOW_STOPPED it is "stopped". */
export const SLOW_KMH = 15;
export const STOPPED_KMH = 5;

/** Seconds a route loses crossing `lengthM` of a slow cell whose flow is
 *  `kmh`, relative to the engine's own average for the route. Never negative:
 *  live traffic can only make an ETA later, not earlier. */
export function delayFor(lengthM: number, kmh: number, routeAvgKmh: number): number {
  if (kmh >= routeAvgKmh || kmh <= 0) return 0;
  const slow = lengthM / (Math.max(kmh, 2) / 3.6);
  const free = lengthM / (routeAvgKmh / 3.6);
  return Math.max(0, slow - free);
}
